import type { PropertyDescriptor } from '../../core/definitions';
import { num, parseColor } from '../shaderWrap';
import { defineShaderFilter } from './define';

// Shared GLSL. Colour maths runs on straight (un-premultiplied) values and the
// result is premultiplied again on the way out.
const LUMA = `
// Rec. 709 luma weights (ITU-R BT.709).
const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);
`;

const GRADE = `
uniform float uGamma;
uniform float uContrast;
uniform float uBrightness;
vec3 grade(vec3 c) {
  c = pow(max(c, 0.0), vec3(uGamma));
  c = (c - 0.5) * uContrast + 0.5;
  return c + uBrightness;
}
`;

/** Slider value (-3..3, 0 = neutral) to the exponent applied to the colour. */
function gammaExponent(value: number): number {
  return value < 0 ? 1 - value : 1 / (1 + value);
}

/** Slider value (-4..4, 0 = neutral) to the slope about mid grey. */
function contrastFactor(value: number): number {
  return value < 0 ? 1 / (1 - value) : 1 + value;
}

function rgb(value: unknown): number[] {
  return parseColor(value).slice(0, 3);
}

const slider = (
  key: string,
  label: string,
  min: number,
  max: number,
  step: number,
  unit?: string,
): PropertyDescriptor => ({ type: 'number', key, label, min, max, step, slider: true, unit });

const gradeProperties: PropertyDescriptor[] = [
  slider('opacity', 'Opacity', 0, 1, 0.01),
  slider('contrast', 'Contrast', -4, 4, 0.01),
  slider('brightness', 'Brightness', -1, 1, 0.001),
  slider('gamma', 'Gamma', -3, 3, 0.01),
];

type ColorCorrectionSettings = {
  gamma: number;
  contrast: number;
  brightness: number;
  saturation: number;
  hueShift: number;
  opacity: number;
  colorMultiply: string;
  colorAdd: string;
};

export const colorCorrection = defineShaderFilter<ColorCorrectionSettings>({
  kind: 'color-correction',
  displayName: 'Color Correction',
  defaults: () => ({
    gamma: 0,
    contrast: 0,
    brightness: 0,
    saturation: 0,
    hueShift: 0,
    opacity: 1,
    colorMultiply: '#ffffff',
    colorAdd: '#000000',
  }),
  properties: () => [
    slider('gamma', 'Gamma', -3, 3, 0.01),
    slider('contrast', 'Contrast', -4, 4, 0.01),
    slider('brightness', 'Brightness', -1, 1, 0.001),
    slider('saturation', 'Saturation', -1, 5, 0.01),
    slider('hueShift', 'Hue shift', -180, 180, 0.1, '°'),
    slider('opacity', 'Opacity', 0, 1, 0.01),
    { type: 'color', key: 'colorMultiply', label: 'Color multiply' },
    { type: 'color', key: 'colorAdd', label: 'Color add' },
  ],
  passes: [
    `${LUMA}${GRADE}
uniform float uSaturation;
uniform float uHue;
uniform float uOpacity;
uniform vec3 uMultiply;
uniform vec3 uAdd;
// Rodrigues' rotation formula about the grey axis (1,1,1)/sqrt(3).
vec3 hueRotate(vec3 c, float angle) {
  const vec3 k = vec3(0.5773502691896258);
  float cs = cos(angle);
  float sn = sin(angle);
  return c * cs + cross(k, c) * sn + k * dot(k, c) * (1.0 - cs);
}
void main() {
  vec4 src = texture(uSource, vUv);
  if (src.a <= 0.0) { fragColor = vec4(0.0); return; }
  vec3 c = grade(src.rgb / src.a);
  c = mix(vec3(dot(c, LUMA)), c, uSaturation);
  c = hueRotate(c, uHue);
  c = c * uMultiply + uAdd;
  float a = src.a * uOpacity;
  fragColor = vec4(clamp(c, 0.0, 1.0) * a, a);
}
`,
  ],
  uniforms: (s) => ({
    uGamma: gammaExponent(num(s.gamma, 0)),
    uContrast: contrastFactor(num(s.contrast, 0)),
    uBrightness: num(s.brightness, 0),
    uSaturation: Math.max(0, num(s.saturation, 0) + 1),
    uHue: (num(s.hueShift, 0) * Math.PI) / 180,
    uOpacity: num(s.opacity, 1),
    uMultiply: rgb(s.colorMultiply),
    uAdd: rgb(s.colorAdd),
  }),
});

type ChromaKeySettings = {
  keyColorType: string;
  keyColor: string;
  similarity: number;
  smoothness: number;
  spill: number;
  opacity: number;
  contrast: number;
  brightness: number;
  gamma: number;
};

const KEY_PRESETS: Record<string, string> = { green: '#00ff00', blue: '#0000ff', magenta: '#ff00ff' };

// Same matrix as the cbcr() function in the shader.
function cbcr(color: number[]): number[] {
  const y = 0.2126 * color[0]! + 0.7152 * color[1]! + 0.0722 * color[2]!;
  return [(color[2]! - y) / 1.8556, (color[0]! - y) / 1.5748];
}

export const chromaKey = defineShaderFilter<ChromaKeySettings>({
  kind: 'chroma-key',
  displayName: 'Chroma Key',
  defaults: () => ({
    keyColorType: 'green',
    keyColor: '#00ff00',
    similarity: 0.4,
    smoothness: 0.08,
    spill: 0.1,
    opacity: 1,
    contrast: 0,
    brightness: 0,
    gamma: 0,
  }),
  properties: () => [
    {
      type: 'select',
      key: 'keyColorType',
      label: 'Key color',
      options: [
        { value: 'green', label: 'Green' },
        { value: 'blue', label: 'Blue' },
        { value: 'magenta', label: 'Magenta' },
        { value: 'custom', label: 'Custom' },
      ],
    },
    { type: 'color', key: 'keyColor', label: 'Custom color', visibleWhen: (s) => s.keyColorType === 'custom' },
    slider('similarity', 'Similarity', 0, 1, 0.001),
    slider('smoothness', 'Smoothness', 0, 1, 0.001),
    slider('spill', 'Key color spill reduction', 0, 1, 0.001),
    ...gradeProperties,
  ],
  passes: [
    `${LUMA}${GRADE}
uniform vec2 uKey;
uniform float uSimilarity;
uniform float uSmoothness;
uniform float uSpill;
uniform float uOpacity;
// Chroma key by distance in the CbCr plane of YCbCr (BT.709 colour-difference
// scaling), so brightness variations of the backdrop do not affect the matte.
vec2 cbcr(vec3 c) {
  float y = dot(c, LUMA);
  return vec2((c.b - y) / 1.8556, (c.r - y) / 1.5748);
}
void main() {
  vec4 src = texture(uSource, vUv);
  if (src.a <= 0.0) { fragColor = vec4(0.0); return; }
  vec3 c = src.rgb / src.a;
  float dist = distance(cbcr(c), uKey);
  float matte = clamp((dist - uSimilarity) / max(uSmoothness, 1e-5), 0.0, 1.0);
  float edge = uSimilarity + uSmoothness;
  float spill = uSpill > 0.0 ? 1.0 - clamp((dist - edge) / uSpill, 0.0, 1.0) : 0.0;
  c = mix(c, vec3(dot(c, LUMA)), spill);
  c = grade(c);
  float a = src.a * matte * uOpacity;
  fragColor = vec4(clamp(c, 0.0, 1.0) * a, a);
}
`,
  ],
  uniforms: (s) => ({
    uKey: cbcr(rgb(KEY_PRESETS[s.keyColorType] ?? s.keyColor)),
    uSimilarity: num(s.similarity, 0.4),
    uSmoothness: num(s.smoothness, 0.08),
    uSpill: num(s.spill, 0.1),
    uOpacity: num(s.opacity, 1),
    uGamma: gammaExponent(num(s.gamma, 0)),
    uContrast: contrastFactor(num(s.contrast, 0)),
    uBrightness: num(s.brightness, 0),
  }),
});

type ColorKeySettings = { keyColor: string; similarity: number; smoothness: number };

export const colorKey = defineShaderFilter<ColorKeySettings>({
  kind: 'color-key',
  displayName: 'Color Key',
  defaults: () => ({ keyColor: '#00ff00', similarity: 0.3, smoothness: 0.05 }),
  properties: () => [
    { type: 'color', key: 'keyColor', label: 'Key color' },
    slider('similarity', 'Similarity', 0, 1, 0.001),
    slider('smoothness', 'Smoothness', 0, 1, 0.001),
  ],
  passes: [
    `
uniform vec3 uKey;
uniform float uSimilarity;
uniform float uSmoothness;
void main() {
  vec4 src = texture(uSource, vUv);
  if (src.a <= 0.0) { fragColor = vec4(0.0); return; }
  // Euclidean RGB distance, normalised so black-to-white is 1.
  float dist = distance(src.rgb / src.a, uKey) / 1.7320508;
  float matte = clamp((dist - uSimilarity) / max(uSmoothness, 1e-5), 0.0, 1.0);
  fragColor = src * matte;
}
`,
  ],
  uniforms: (s) => ({
    uKey: rgb(s.keyColor),
    uSimilarity: num(s.similarity, 0.3),
    uSmoothness: num(s.smoothness, 0.05),
  }),
});

type LumaKeySettings = { lumaMin: number; lumaMax: number; lumaMinSmooth: number; lumaMaxSmooth: number };

export const lumaKey = defineShaderFilter<LumaKeySettings>({
  kind: 'luma-key',
  displayName: 'Luma Key',
  defaults: () => ({ lumaMin: 0, lumaMax: 1, lumaMinSmooth: 0, lumaMaxSmooth: 0 }),
  properties: () => [
    slider('lumaMax', 'Luma max', 0, 1, 0.001),
    slider('lumaMaxSmooth', 'Luma max smoothness', 0, 1, 0.001),
    slider('lumaMin', 'Luma min', 0, 1, 0.001),
    slider('lumaMinSmooth', 'Luma min smoothness', 0, 1, 0.001),
  ],
  passes: [
    `${LUMA}
uniform float uMin;
uniform float uMax;
uniform float uMinSmooth;
uniform float uMaxSmooth;
void main() {
  vec4 src = texture(uSource, vUv);
  if (src.a <= 0.0) { fragColor = vec4(0.0); return; }
  float luma = dot(src.rgb / src.a, LUMA);
  // Opaque inside [min, max]; each edge fades out over its smoothness width.
  float low = uMinSmooth > 0.0 ? clamp((luma - uMin + uMinSmooth) / uMinSmooth, 0.0, 1.0) : (luma >= uMin ? 1.0 : 0.0);
  float high = uMaxSmooth > 0.0 ? clamp((uMax + uMaxSmooth - luma) / uMaxSmooth, 0.0, 1.0) : (luma <= uMax ? 1.0 : 0.0);
  fragColor = src * (low * high);
}
`,
  ],
  uniforms: (s) => ({
    uMin: num(s.lumaMin, 0),
    uMax: num(s.lumaMax, 1),
    uMinSmooth: num(s.lumaMinSmooth, 0),
    uMaxSmooth: num(s.lumaMaxSmooth, 0),
  }),
});

type CropPadSettings = { left: number; top: number; right: number; bottom: number };

export const cropPad = defineShaderFilter<CropPadSettings>({
  kind: 'crop-pad',
  displayName: 'Crop/Pad',
  defaults: () => ({ left: 0, top: 0, right: 0, bottom: 0 }),
  properties: () => [
    { type: 'number', key: 'left', label: 'Left', min: 0, step: 1, unit: 'px' },
    { type: 'number', key: 'top', label: 'Top', min: 0, step: 1, unit: 'px' },
    { type: 'number', key: 'right', label: 'Right', min: 0, step: 1, unit: 'px' },
    { type: 'number', key: 'bottom', label: 'Bottom', min: 0, step: 1, unit: 'px' },
  ],
  passes: [
    `
uniform vec4 uCrop;
void main() {
  vec2 p = vUv * uSourceSize;
  bool inside = p.x >= uCrop.x && p.y >= uCrop.y && p.x <= uSourceSize.x - uCrop.z && p.y <= uSourceSize.y - uCrop.w;
  fragColor = inside ? texture(uSource, vUv) : vec4(0.0);
}
`,
  ],
  uniforms: (s) => ({
    uCrop: [Math.max(0, num(s.left, 0)), Math.max(0, num(s.top, 0)), Math.max(0, num(s.right, 0)), Math.max(0, num(s.bottom, 0))],
  }),
  active: (s) => num(s.left, 0) > 0 || num(s.top, 0) > 0 || num(s.right, 0) > 0 || num(s.bottom, 0) > 0,
});

type BlurSettings = { radius: number };

const BLUR_PASS = `
uniform vec2 uDirection;
uniform float uSigma;
uniform float uSpacing;
uniform int uPairs;
// Separable Gaussian. Neighbouring taps are fetched in pairs with one bilinear
// sample placed at their weighted centre, which halves the texture fetches.
float gauss(float x) {
  return exp(-0.5 * x * x / (uSigma * uSigma));
}
void main() {
  vec2 stride = uDirection / uSourceSize;
  vec4 sum = texture(uSource, vUv);
  float total = 1.0;
  for (int k = 1; k <= 16; k++) {
    if (k > uPairs) break;
    float near = float(2 * k - 1) * uSpacing;
    float far = near + uSpacing;
    float wNear = gauss(near);
    float wFar = gauss(far);
    float w = wNear + wFar;
    vec2 offset = stride * ((near * wNear + far * wFar) / w);
    sum += (texture(uSource, vUv + offset) + texture(uSource, vUv - offset)) * w;
    total += 2.0 * w;
  }
  fragColor = sum / total;
}
`;

/** Most tap pairs per side; must match the loop bound in BLUR_PASS. */
const BLUR_MAX_PAIRS = 16;

export const blur = defineShaderFilter<BlurSettings>({
  kind: 'blur',
  displayName: 'Blur',
  defaults: () => ({ radius: 8 }),
  properties: () => [slider('radius', 'Radius', 0, 64, 0.5, 'px')],
  passes: [BLUR_PASS, BLUR_PASS],
  uniforms: (s, pass) => {
    const radius = Math.min(256, Math.max(0, num(s.radius, 8)));
    // sigma = radius / 2, kernel cut at three sigma. Only as many taps as the
    // kernel covers are fetched; past 32 px of reach they are spread out.
    const reach = Math.max(1, radius * 1.5);
    const pairs = Math.min(BLUR_MAX_PAIRS, Math.ceil(reach / 2));
    return {
      uDirection: pass === 0 ? [1, 0] : [0, 1],
      uSigma: Math.max(radius * 0.5, 1e-3),
      uSpacing: Math.max(1, reach / (2 * BLUR_MAX_PAIRS)),
      uPairs: pairs,
    };
  },
  active: (s) => num(s.radius, 8) > 0,
});

type SharpenSettings = { amount: number };

export const sharpen = defineShaderFilter<SharpenSettings>({
  kind: 'sharpen',
  displayName: 'Sharpen',
  defaults: () => ({ amount: 0.5 }),
  properties: () => [slider('amount', 'Amount', 0, 4, 0.01)],
  passes: [
    `
uniform float uAmount;
// Unsharp mask: original + amount * (original - blurred), with a 3x3 binomial blur.
void main() {
  vec2 px = 1.0 / uSourceSize;
  vec4 c = texture(uSource, vUv);
  vec4 edges = texture(uSource, vUv + vec2(px.x, 0.0)) + texture(uSource, vUv - vec2(px.x, 0.0))
             + texture(uSource, vUv + vec2(0.0, px.y)) + texture(uSource, vUv - vec2(0.0, px.y));
  vec4 corners = texture(uSource, vUv + px) + texture(uSource, vUv - px)
               + texture(uSource, vUv + vec2(px.x, -px.y)) + texture(uSource, vUv + vec2(-px.x, px.y));
  vec4 blurred = (c * 4.0 + edges * 2.0 + corners) / 16.0;
  vec3 sharp = c.rgb + (c.rgb - blurred.rgb) * uAmount;
  fragColor = vec4(clamp(sharp, 0.0, c.a), c.a);
}
`,
  ],
  uniforms: (s) => ({ uAmount: Math.max(0, num(s.amount, 0.5)) }),
  active: (s) => num(s.amount, 0.5) > 0,
});

type ScrollSettings = { speedX: number; speedY: number; loop: boolean };

export const scroll = defineShaderFilter<ScrollSettings>({
  kind: 'scroll',
  displayName: 'Scroll',
  defaults: () => ({ speedX: 100, speedY: 0, loop: true }),
  properties: () => [
    { type: 'number', key: 'speedX', label: 'Horizontal speed', min: -2000, max: 2000, step: 1, unit: 'px/s', slider: true, description: 'Positive values move the picture to the left.' },
    { type: 'number', key: 'speedY', label: 'Vertical speed', min: -2000, max: 2000, step: 1, unit: 'px/s', slider: true, description: 'Positive values move the picture up.' },
    { type: 'boolean', key: 'loop', label: 'Loop' },
  ],
  passes: [
    `
uniform vec2 uSpeed;
uniform float uLoop;
void main() {
  vec2 uv = vUv + uSpeed * uTime / uSourceSize;
  if (uLoop > 0.5) {
    fragColor = texture(uSource, fract(uv));
  } else {
    bool inside = uv.x >= 0.0 && uv.y >= 0.0 && uv.x <= 1.0 && uv.y <= 1.0;
    fragColor = inside ? texture(uSource, uv) : vec4(0.0);
  }
}
`,
  ],
  uniforms: (s) => ({ uSpeed: [num(s.speedX, 0), num(s.speedY, 0)], uLoop: s.loop === false ? 0 : 1 }),
});

type OpacitySettings = { opacity: number };

export const opacity = defineShaderFilter<OpacitySettings>({
  kind: 'opacity',
  displayName: 'Opacity',
  defaults: () => ({ opacity: 1 }),
  properties: () => [slider('opacity', 'Opacity', 0, 1, 0.01)],
  passes: [
    `
uniform float uOpacity;
void main() {
  fragColor = texture(uSource, vUv) * uOpacity;
}
`,
  ],
  uniforms: (s) => ({ uOpacity: Math.min(1, Math.max(0, num(s.opacity, 1))) }),
  active: (s) => num(s.opacity, 1) < 1,
});

type VignetteSettings = { amount: number; radius: number; softness: number };

export const vignette = defineShaderFilter<VignetteSettings>({
  kind: 'vignette',
  displayName: 'Vignette',
  defaults: () => ({ amount: 0.5, radius: 0.75, softness: 0.45 }),
  properties: () => [
    slider('amount', 'Amount', 0, 1, 0.01),
    slider('radius', 'Radius', 0, 1.5, 0.01),
    slider('softness', 'Softness', 0.01, 1, 0.01),
  ],
  passes: [
    `
uniform float uAmount;
uniform float uRadius;
uniform float uSoftness;
void main() {
  vec4 src = texture(uSource, vUv);
  // Distance from the centre, 1.0 at the corners.
  float d = length(vUv - 0.5) / 0.70710678;
  float shade = 1.0 - uAmount * smoothstep(uRadius - uSoftness, uRadius, d);
  fragColor = vec4(src.rgb * shade, src.a);
}
`,
  ],
  uniforms: (s) => ({
    uAmount: num(s.amount, 0.5),
    uRadius: num(s.radius, 0.75),
    uSoftness: Math.max(0.001, num(s.softness, 0.45)),
  }),
  active: (s) => num(s.amount, 0.5) > 0,
});

type PixelateSettings = { size: number };

export const pixelate = defineShaderFilter<PixelateSettings>({
  kind: 'pixelate',
  displayName: 'Pixelate',
  defaults: () => ({ size: 8 }),
  properties: () => [slider('size', 'Block size', 1, 128, 1, 'px')],
  passes: [
    `
uniform float uBlock;
void main() {
  vec2 cell = (floor(vUv * uSourceSize / uBlock) + 0.5) * uBlock;
  fragColor = texture(uSource, min(cell, uSourceSize - 0.5) / uSourceSize);
}
`,
  ],
  uniforms: (s) => ({ uBlock: Math.max(1, num(s.size, 8)) }),
  active: (s) => num(s.size, 8) > 1,
});
